// utils/deadline.ts
export type ParsedDeadline = {
    /** Числовой ключ сортировки/сравнения в месяцах: YYYY*12 + MM (1-12). null — если неизвестно */
    sortKey: number | null;
    /** Год, если распознан */
    year: number | null;
    /** Месяц (1-12). Если указан квартал — берём последний месяц квартала для безопасности сравнения (мягкий дедлайн) */
    month: number | null;
    /** Квартал 1-4, если был */
    quarter: 1 | 2 | 3 | 4 | null;
    /** Человекочитаемый ярлык, который можно показывать в UI */
    label: string;
    /** Признак «не указано» */
    unknown: boolean;
};

const QUARTER_END_MONTH = { 1: 3, 2: 6, 3: 9, 4: 12 } as const;

const monthByRu: Record<string, number> = {
    'январь': 1, 'января': 1,
    'февраль': 2, 'февраля': 2,
    'март': 3, 'марта': 3,
    'апрель': 4, 'апреля': 4,
    'май': 5, 'мая': 5,
    'июнь': 6, 'июня': 6,
    'июль': 7, 'июля': 7,
    'август': 8, 'августа': 8,
    'сентябрь': 9, 'сентября': 9,
    'октябрь': 10, 'октября': 10,
    'ноябрь': 11, 'ноября': 11,
    'декабрь': 12, 'декабря': 12,
};

const norm = (s: string) =>
    (s || '')
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .replace(/[.,]/g, '')
        .trim();

/**
 * Парсит строку дедлайна в единый формат сравнения.
 * Поддерживает:
 *  - "2026 г", "2026 год", "2027" (берём декабрь как крайний срок)
 *  - "3 кв 2025 г", "3 квартал 2027 год" (берём последний месяц квартала)
 *  - "декабрь 2027", "июль 2026 года" (если вдруг встретится)
 *  - пусто => unknown
 */
export function parseDeadline(raw: string | null | undefined): ParsedDeadline {
    const s = norm(raw ?? '');

    if (!s) {
        return { sortKey: null, year: null, month: null, quarter: null, label: 'Не указано', unknown: true };
    }

    // Квартал: "3 кв 2025 г", "3 квартал 2027 год"
    {
        const m = s.match(/([1-4])\s*кв(?:артал)?\s*(\d{4})/);
        if (m) {
            const q = Number(m[1]) as 1 | 2 | 3 | 4;
            const y = Number(m[2]);
            const month = QUARTER_END_MONTH[q];
            const sortKey = y * 12 + month;
            return {
                sortKey, year: y, month, quarter: q,
                label: `${q} кв ${y}`,
                unknown: false,
            };
        }
    }

    // Месяц + год: "декабрь 2027", "июль 2026 года"
    {
        const m = s.match(
            new RegExp(`(${Object.keys(monthByRu).join('|')})\\s+(\\d{4})`)
        );
        if (m) {
            const monthName = m[1];
            const y = Number(m[2]);
            const month = monthByRu[monthName];
            const sortKey = y * 12 + month;
            return {
                sortKey, year: y, month, quarter: null,
                label: `${capFirst(monthName)} ${y}`,
                unknown: false,
            };
        }
    }

    // Просто год: "2026", "2026 г", "2026 год"
    {
        const m = s.match(/(\d{4})\s*(?:г|год)?$/);
        if (m) {
            const y = Number(m[1]);
            const month = 12; // считаем сдачу до конца года
            const sortKey = y * 12 + month;
            return {
                sortKey, year: y, month, quarter: null,
                label: `${y}`,
                unknown: false,
            };
        }
    }

    // Не распознали — считаем неизвестным
    return { sortKey: null, year: null, month: null, quarter: null, label: raw || 'Не указано', unknown: true };
}

function capFirst(s: string) {
    return s.charAt(0).toUpperCase() + s.slice(1);
}
